import { useState, useCallback, useRef } from "react";
import BucketApi from "api/bucketApi";

const useComunicacionEditorImages = (procesoIdentificador, comunicacionId = null) => {
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [tempImages, setTempImages] = useState([]); // Para imágenes temporales antes de crear la comunicación
  const imageCountersRef = useRef({});

  const setupImageHandler = useCallback(
    (quillRef, campoNombre = "contenido", onImageUploaded = null) => {
      if (!quillRef.current) return;
      const quill = quillRef.current.getEditor();
      const toolbar = quill.getModule("toolbar");

      toolbar.addHandler("image", () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.onchange = async () => {
          const file = input.files[0];
          if (file) {
            await handleImageUpload(file, quillRef, campoNombre, onImageUploaded);
          }
        };
        input.click();
      });
    },
    [comunicacionId, procesoIdentificador]
  );

  const handleImageUpload = useCallback(
    async (file, quillRef, campoNombre = "contenido", onImageUploaded = null) => {
      setImageError(null);
      const validation = BucketApi.validateImageFile(file);
      if (!validation.isValid) {
        setImageError(validation.errors.join(" "));
        return false;
      }

      try {
        setUploadingImage(true);

        // Redimensionar si es necesario
        let processedFile = file;
        if (file.size > 1024 * 1024) {
          processedFile = await BucketApi.resizeImage(file, 800, 600, 0.8);
        }

        // Si no tenemos comunicacionId aún, almacenar temporalmente
        if (!comunicacionId) {
          const tempId = Date.now();
          const tempUrl = URL.createObjectURL(processedFile);

          // Insertar en Quill con URL temporal
          if (quillRef?.current) {
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection();
            const index = range ? range.index : 0;
            quill.insertEmbed(index, "image", tempUrl);
            quill.setSelection(index + 1);
          }

          // Guardar info temporal
          const tempImageInfo = {
            tempId,
            file: processedFile,
            originalName: file.name,
            tempUrl,
            campo: campoNombre,
            uploadedAt: new Date().toISOString(),
          };

          setTempImages((prev) => [...prev, tempImageInfo]);
          console.log("Imagen temporal guardada:", tempImageInfo);

          if (onImageUploaded) {
            try {
              onImageUploaded(tempImageInfo);
            } catch (cbErr) {
              console.warn("Error en onImageUploaded callback:", cbErr);
            }
          }
          return true;
        }

        // Si ya tenemos comunicacionId, subir directamente
        const result = await uploadImageToBucket(processedFile, file.name, campoNombre);
        if (result.success) {
          // Insertar en Quill con URL final
          if (quillRef?.current) {
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection();
            const index = range ? range.index : 0;
            quill.insertEmbed(index, "image", result.imageUrl);
            quill.setSelection(index + 1);
          }

          // Guardar info en estado
          const imageInfo = {
            fileName: result.fileName,
            originalName: file.name,
            url: result.imageUrl,
            campo: campoNombre,
            uploadedAt: new Date().toISOString(),
          };

          setUploadedImages((prev) => [...prev, imageInfo]);
          console.log("Imagen subida exitosamente:", imageInfo);

          if (onImageUploaded) {
            try {
              onImageUploaded(imageInfo);
            } catch (cbErr) {
              console.warn("Error en onImageUploaded callback:", cbErr);
            }
          }
        }

        return result.success;
      } catch (error) {
        console.error("Error subiendo imagen:", error);
        setImageError("Error al subir la imagen. Por favor intenta nuevamente.");
        return false;
      } finally {
        setUploadingImage(false);
      }
    },
    [procesoIdentificador, comunicacionId]
  );

  const uploadImageToBucket = useCallback(
    async (file, originalFileName, campoNombre = "contenido") => {
      try {
        console.log("=== SUBIENDO IMAGEN AL BUCKET ===");
        console.log("Archivo:", originalFileName);
        console.log("Proceso:", procesoIdentificador);
        console.log("Comunicación:", comunicacionId);

        // Obtener todas las imágenes existentes de la comunicación
        let allComunicacionImages = [];
        try {
          const allImagesResponse = await BucketApi.getAllComunicacionImages(
            procesoIdentificador,
            comunicacionId
          );
          if (allImagesResponse?.data) {
            let allImagesData = allImagesResponse.data;
            if (Array.isArray(allImagesData)) {
              allComunicacionImages = allImagesData;
            } else if (allImagesData?.files) {
              allComunicacionImages = allImagesData.files;
            } else if (allImagesData?.objects) {
              allComunicacionImages = allImagesData.objects;
            }
            allComunicacionImages = allComunicacionImages
              .map((item) => {
                if (typeof item === "string") return item.split("/").pop().split("?")[0];
                if (item?.name) return item.name.split("/").pop().split("?")[0];
                if (item?.key) return item.key.split("/").pop().split("?")[0];
                return null;
              })
              .filter(Boolean);
          }
        } catch (error) {
          console.warn("No se pudo obtener la lista completa de imágenes:", error);
        }

        const comunicacionPrefix = `${comunicacionId}_${campoNombre}_`;
        const getConsecutivoFromName = (fileName) => {
          if (!fileName.startsWith(comunicacionPrefix)) return null;
          const nameWithoutPrefix = fileName.substring(comunicacionPrefix.length);
          const parts = nameWithoutPrefix.split(".");
          return Number.isFinite(parseInt(parts[0], 10)) ? parseInt(parts[0], 10) : null;
        };

        const existingConsecutivos = allComunicacionImages
          .map(getConsecutivoFromName)
          .filter((n) => Number.isFinite(n));

        let nextConsecutivo = 1;
        while (existingConsecutivos.includes(nextConsecutivo)) {
          nextConsecutivo++;
        }

        const extension = originalFileName.split(".").pop().toLowerCase();
        const fileName = `${comunicacionId}_${campoNombre}_${nextConsecutivo}.${extension}`;

        // 🔹 Aquí fijamos el folderPath
        const folderPath = `procesos/${procesoIdentificador}/comunicaciones/${comunicacionId}/imagenes/`;

        console.log("Nombre final del archivo:", fileName);
        console.log("FolderPath destino:", folderPath);

        // Subir imagen al bucket con ruta fija
        const uploadResponse = await BucketApi.uploadComunicacionImage(
          file,
          fileName,
          procesoIdentificador,
          comunicacionId,
          folderPath
        );

        if (!uploadResponse.data?.file_url) {
          throw new Error("No se recibió la URL del archivo subido");
        }

        return {
          success: true,
          fileName,
          imageUrl: uploadResponse.data.file_url,
        };
      } catch (error) {
        console.error("Error subiendo imagen al bucket:", error);
        return { success: false, error: error.message };
      }
    },
    [procesoIdentificador, comunicacionId, uploadedImages]
  );

  const processTempImages = useCallback(
    async (newComunicacionId, htmlContent) => {
      if (!tempImages.length || !newComunicacionId) {
        return htmlContent;
      }

      console.log("Procesando imágenes temporales para comunicación:", newComunicacionId);
      let updatedContent = htmlContent;

      for (const tempImage of tempImages) {
        try {
          // Subir la imagen temporal al bucket
          const result = await uploadImageToBucket(
            tempImage.file,
            tempImage.originalName,
            tempImage.campo
          );

          if (result.success) {
            // Reemplazar la URL temporal con la URL final en el contenido
            updatedContent = updatedContent.replace(tempImage.tempUrl, result.imageUrl);

            // Limpiar la URL temporal
            URL.revokeObjectURL(tempImage.tempUrl);

            console.log(`Imagen temporal ${tempImage.originalName} procesada correctamente`);
          } else {
            console.error(
              `Error procesando imagen temporal ${tempImage.originalName}:`,
              result.error
            );
          }
        } catch (error) {
          console.error(`Error procesando imagen temporal:`, error);
        }
      }

      // Limpiar imágenes temporales
      setTempImages([]);
      return updatedContent;
    },
    [tempImages, uploadImageToBucket]
  );

  const extractImageUrls = useCallback(
    (htmlContent) => {
      if (!htmlContent) return [];
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");
      const images = doc.querySelectorAll("img");

      const baseFolder = `procesos/${procesoIdentificador}/comunicaciones/${comunicacionId}/imagenes/`;
      return Array.from(images)
        .map((img) => img.getAttribute("src"))
        .filter((src) => {
          if (!src) return false;
          const containsBaseFolder = src.includes(baseFolder);
          console.log("Verificando imagen:", src, "contiene base folder:", containsBaseFolder);
          return containsBaseFolder;
        });
    },
    [procesoIdentificador, comunicacionId]
  );

  const cleanOrphanImages = useCallback(
    async (contenidoCampos) => {
      if (!comunicacionId) {
        console.warn("No se puede limpiar imágenes sin comunicacionId");
        return { success: false, error: "comunicacionId requerido" };
      }

      try {
        console.log("Iniciando limpieza de imágenes huérfanas de comunicación...");

        const allContent = Object.values(contenidoCampos).join(" ");
        const currentImageUrls = extractImageUrls(allContent);
        console.log("URLs de imágenes encontradas en el contenido:", currentImageUrls);

        const currentFileNames = currentImageUrls
          .map((url) => {
            const fileName = url.split("/").pop().split("?")[0];
            console.log("Procesando URL:", url, "-> fileName:", fileName);
            return fileName;
          })
          .filter(Boolean);

        console.log("Nombres de archivos a conservar:", currentFileNames);

        const result = await BucketApi.cleanOrphanComunicacionImages(
          procesoIdentificador,
          comunicacionId,
          currentFileNames
        );

        console.log("Resultado de limpieza:", result.data);
        return {
          success: true,
          imagenes_conservadas: currentFileNames.length,
          ...result.data,
        };
      } catch (error) {
        console.error("Error limpiando imágenes huérfanas:", error);
        return {
          success: false,
          error: error.message,
        };
      }
    },
    [procesoIdentificador, comunicacionId, extractImageUrls]
  );

  const processImagesForPDF = useCallback(
    async (htmlContent) => {
      if (!htmlContent || !comunicacionId) return htmlContent;

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");
      const images = doc.querySelectorAll("img");
      const baseFolder = `procesos/${procesoIdentificador}/comunicaciones/${comunicacionId}/imagenes/`;

      for (let img of images) {
        try {
          const src = img.getAttribute("src");
          if (src && src.includes(baseFolder)) {
            const response = await fetch(src);
            const blob = await response.blob();
            const base64 = await BucketApi.convertToBase64(blob);
            img.setAttribute("src", base64);
            const fileName = src.split("/").pop().split("?")[0];
            img.setAttribute("data-filename", fileName);
          }
        } catch (error) {
          console.error("Error procesando imagen para PDF:", error);
        }
      }

      return doc.body.innerHTML;
    },
    [procesoIdentificador, comunicacionId]
  );

  const getQuillModules = useCallback(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
      ],
      clipboard: { matchVisual: false },
    }),
    []
  );

  const getQuillFormats = useCallback(
    () => ["header", "bold", "italic", "underline", "list", "bullet", "indent", "link", "image"],
    []
  );

  const resetImageCounters = useCallback(() => {
    imageCountersRef.current = {};
  }, []);

  const clearTempImages = useCallback(() => {
    // Limpiar URLs temporales para evitar memory leaks
    tempImages.forEach((tempImage) => {
      URL.revokeObjectURL(tempImage.tempUrl);
    });
    setTempImages([]);
  }, [tempImages]);

  return {
    uploadingImage,
    imageError,
    uploadedImages,
    tempImages,
    setupImageHandler,
    handleImageUpload,
    processTempImages,
    processImagesForPDF,
    extractImageUrls,
    cleanOrphanImages,
    resetImageCounters,
    clearTempImages,
    getQuillModules,
    getQuillFormats,
    clearError: () => setImageError(null),
    clearUploadedImages: () => setUploadedImages([]),
  };
};

export default useComunicacionEditorImages;
